import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "../../components/TopBar";
import { Modal } from "../../components/Modal";
import { useProjects } from "../../context/ProjectContext";
import { useToast } from "../../context/ToastContext";
import { api } from "../../api/client";
import { formatScore } from "../../api/defaults";
import { timeAgo, shortDate, windowLabel } from "../../lib/format";
import type { BoardVersion, Entry, Leaderboard } from "../../api/types";

const POLL_MS = 4000;

const UPDATE_METHOD_LABELS: Record<string, string> = {
  keep_best: "Keeps best score",
  force: "Latest score",
  increment: "Adds up",
  aggregate: "Aggregated",
};

const TIEBREAKER_LABELS: Record<string, string> = {
  subscore: "Subscore",
  earliest_submission: "Earliest submission",
  latest_submission: "Latest submission",
};

/** Human-readable tiebreaker chain. Drops the internal `id` term — it's the lexicographic
 *  fallback always applied last so ranks are deterministic; operators don't configure or care
 *  about it, so it stays out of the UI. */
function tiebreakerLabel(tiebreakers: string[]): string {
  const shown = tiebreakers.filter((t) => t !== "id").map((t) => TIEBREAKER_LABELS[t] ?? t);
  return shown.length ? shown.join(" → ") : "Submission order";
}

export function BoardInspector() {
  const { boardId } = useParams();
  const { active } = useProjects();
  const pid = active?.id ?? "";
  const toast = useToast();
  const nav = useNavigate();

  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [versions, setVersions] = useState<BoardVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);
  const [flash, setFlash] = useState<Record<string, number>>({});
  const [submitOpen, setSubmitOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Phase 11.4: a board group exposes its windows as variants of one leaderboard. The active
  // window selects which child board (`<id>:<window>`) the live view reads from.
  const [activeWindow, setActiveWindow] = useState<string | null>(null);
  const prevRanks = useRef<Record<string, number>>({});

  const isGroup = !!(board?.windows && board.windows.length);
  const effectiveId = isGroup && activeWindow ? `${board!.id}:${activeWindow}` : (boardId ?? "");

  const refresh = useCallback(async () => {
    if (!effectiveId || !pid) return;
    const [top, c] = await Promise.all([
      api.top(pid, effectiveId, 50),
      api.count(pid, effectiveId),
    ]);
    // flash rows whose rank changed since last poll
    const changed: Record<string, number> = {};
    top.forEach((e) => {
      const prev = prevRanks.current[e.player_id];
      if (prev !== undefined && prev !== e.rank) changed[e.player_id] = Date.now();
      prevRanks.current[e.player_id] = e.rank;
    });
    if (Object.keys(changed).length) {
      setFlash((f) => ({ ...f, ...changed }));
      setTimeout(() => setFlash({}), 900);
    }
    setEntries(top);
    setCount(c);
  }, [effectiveId, pid]);

  // Load the board config once; for a group, default to the all-time window (or the first).
  useEffect(() => {
    if (!boardId || !pid) return;
    setLoading(true);
    api.getBoard(pid, boardId)
      .then((b) => {
        setBoard(b);
        if (b.windows && b.windows.length) {
          setActiveWindow(b.windows.includes("all_time") ? "all_time" : b.windows[0]);
        }
      })
      .catch((e) => toast((e as Error).message, "err"))
      .finally(() => setLoading(false));
  }, [boardId, pid, toast]);

  // (Re)load entries + versions whenever the effective board (selected window) changes.
  useEffect(() => {
    if (!effectiveId || !pid) return;
    prevRanks.current = {};
    api.listVersions(pid, effectiveId).then(setVersions).catch(() => {});
    refresh();
  }, [effectiveId, pid, refresh]);

  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => { refresh(); }, POLL_MS);
    return () => clearInterval(t);
  }, [live, refresh]);

  async function deleteEntry(playerId: string) {
    if (!effectiveId) return;
    try {
      await api.deleteEntry(pid, effectiveId, playerId);
      toast(`Removed ${playerId}`);
      refresh();
    } catch (e) {
      toast((e as Error).message, "err");
    }
  }

  async function deleteBoard() {
    if (!board) return;
    try {
      // A group: delete every child window board, then the parent marker.
      if (board.windows && board.windows.length) {
        for (const w of board.windows) {
          await api.deleteBoard(pid, `${board.id}:${w}`);
        }
      }
      await api.deleteBoard(pid, board.id);
      toast(`Board "${board.id}" deleted`);
      nav("/app/boards");
    } catch (e) {
      toast((e as Error).message, "err");
    }
  }

  if (loading || !board) {
    return (
      <>
        <TopBar crumbs={<b>Loading…</b>} />
        <div className="content"><div className="card" style={{ padding: 30 }}><div className="skel" style={{ height: 220 }} /></div></div>
      </>
    );
  }

  return (
    <>
      <TopBar
        crumbs={<><Link to="/app/boards" className="muted">Leaderboards</Link> / <b>{board.name || board.id}</b></>}
        actions={
          <>
            <Link to={`/app/boards/${encodeURIComponent(board.id)}/edit`} className="btn btn-ghost btn-sm">Configure</Link>
            <button className="btn btn-primary btn-sm" onClick={() => setSubmitOpen(true)}>＋ Submit score</button>
          </>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1 style={{ fontSize: 26 }}>{board.name || board.id}</h1>
            <div className="mono faint" style={{ fontSize: 13, marginTop: 2 }}>{board.id}</div>
            <div className="row wrap" style={{ marginTop: 12, gap: 8 }}>
              <span className="tag tag-dim">{board.sort_order === "asc" ? "Lowest wins" : "Highest wins"}</span>
              <span className="tag tag-mint">{UPDATE_METHOD_LABELS[board.update_method] ?? board.update_method}{board.update_method === "aggregate" && board.aggregation ? `: ${board.aggregation}` : ""}</span>
              {!isGroup && board.reset && <span className="tag tag-violet">{board.reset.interval} reset</span>}
              <span className="tag tag-dim">{board.visibility === "public" ? "Public" : "Private"}</span>
            </div>
            {isGroup && (
              <div className="row wrap" style={{ marginTop: 14, gap: 8 }}>
                <span className="faint" style={{ fontSize: 12.5, alignSelf: "center", marginRight: 2 }}>Window:</span>
                {board.windows!.map((w) => (
                  <button
                    key={w}
                    className={`btn btn-sm ${w === activeWindow ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setActiveWindow(w)}
                  >
                    {windowLabel(w)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* stat row */}
        <div className="stat-grid">
          <div className="card stat-card"><div className="lbl">Entries</div><div className="val">{(count ?? board.size).toLocaleString()}</div></div>
          <div className="card stat-card"><div className="lbl">Recent updates (24h)</div><div className="val">{board.recent_updates != null ? board.recent_updates.toLocaleString() : "—"}</div></div>
          <div className="card stat-card"><div className="lbl">Last activity</div><div className="val" style={{ fontSize: 18 }}>{timeAgo(board.last_activity_at ?? board.updated_at)}</div></div>
          <div className="card stat-card"><div className="lbl">Max entries</div><div className="val" style={{ fontSize: 24 }}>{board.max_entries ? board.max_entries.toLocaleString() : "∞"}</div></div>
        </div>

        <div className="insp-grid" style={{ marginTop: 22 }}>
          {/* LIVE TOP-N */}
          <div className="card" style={{ padding: "4px 0" }}>
            <div className="between" style={{ padding: "14px 20px" }}>
              <div className="row" style={{ gap: 10 }}>
                <h3 style={{ fontSize: 18 }}>Top entries{isGroup && activeWindow ? ` · ${windowLabel(activeWindow)}` : ""}</h3>
                {live && <span className="tag tag-mint"><span className="dot" /> live</span>}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setLive((l) => !l)}>{live ? "Pause" : "Resume"}</button>
                <button className="btn btn-ghost btn-sm" onClick={() => refresh()}>↻</button>
              </div>
            </div>
            {entries.length === 0 ? (
              <p className="muted" style={{ padding: "8px 20px 22px" }}>No entries yet. Submit a score to populate the board.</p>
            ) : (
              <table className="tbl">
                <thead><tr><th>#</th><th>Player</th><th>Score</th><th></th></tr></thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.player_id} className={`${e.rank === 1 ? "rank-1" : ""} ${flash[e.player_id] ? "flash-row" : ""}`}>
                      <td className="num">{e.rank === 1 ? "🏆" : e.rank}</td>
                      <td>
                        <span className="mono">{e.player_id}</span>
                        {e.metadata && typeof (e.metadata as { country?: string }).country === "string" && (
                          <span className="faint" style={{ marginLeft: 8, fontSize: 12 }}>{(e.metadata as { country?: string }).country}</span>
                        )}
                      </td>
                      <td className="num" style={{ color: "var(--mint)" }}>{formatScore(e.score, board.display_as)}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn btn-danger btn-sm" title="Remove entry (GDPR / ban)" onClick={() => deleteEntry(e.player_id)}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* SIDE: config + versions + danger */}
          <div className="stack" style={{ gap: 18 }}>
            <div className="card" style={{ padding: "16px 20px" }}>
              <h3 style={{ fontSize: 16, marginBottom: 10 }}>Configuration</h3>
              <div className="kv"><span className="k">Tiebreakers</span><span className="v">{tiebreakerLabel(board.tiebreaker)}</span></div>
              <div className="kv"><span className="k">Who can submit</span><span className="v">{board.write_auth === "client_signed" ? "Players (client tokens)" : "Server only (API key)"}</span></div>
              <div className="kv"><span className="k">Rate limit</span><span className="v">{board.rate_limit ? `${board.rate_limit}/min` : "none"}</span></div>
              <div className="kv"><span className="k">Archive</span><span className="v">{board.archive_policy.kind}{board.archive_policy.keep_last_n ? ` (${board.archive_policy.keep_last_n})` : ""}</span></div>
              <div className="kv"><span className="k">Created</span><span className="v">{shortDate(board.created_at)}</span></div>
            </div>

            <div className="card" style={{ padding: "16px 20px" }}>
              <h3 style={{ fontSize: 16, marginBottom: 10 }}>Seasons</h3>
              {versions.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>No archived seasons.</p> : (
                <div className="stack" style={{ gap: 6 }}>
                  {versions.map((v) => (
                    <div key={v.version} className="kv">
                      <span className="k">Season {v.version} {v.archived_at === null && <span className="tag tag-mint" style={{ marginLeft: 6 }}>current</span>}</span>
                      <span className="v">{v.size.toLocaleString()} · {v.archived_at ? shortDate(v.archived_at) : "live"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card" style={{ padding: "16px 20px", borderColor: "rgba(255,107,74,0.3)" }}>
              <h3 style={{ fontSize: 16, marginBottom: 6 }}>Danger zone</h3>
              <p className="faint" style={{ fontSize: 12.5, marginBottom: 12 }}>
                {isGroup
                  ? `Deletes this group and all ${board.windows!.length} window boards. Cannot be undone.`
                  : "Deletes the board and all archived seasons. Cannot be undone."}
              </p>
              <button className="btn btn-danger btn-sm" onClick={() => setDeleteOpen(true)}>Delete leaderboard</button>
            </div>
          </div>
        </div>
      </div>

      {submitOpen && <SubmitModal board={board} pid={pid} onClose={() => setSubmitOpen(false)} onDone={() => { setSubmitOpen(false); refresh(); }} />}
      {deleteOpen && (
        <Modal title="Delete leaderboard?" onClose={() => setDeleteOpen(false)}>
          <div className="modal-body">
            <div className="warn-box">This permanently deletes <b className="mono">{board.id}</b> and every archived season. This cannot be undone.</div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setDeleteOpen(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={deleteBoard}>Delete forever</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function SubmitModal({
  board, pid, onClose, onDone,
}: { board: Leaderboard; pid: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [player, setPlayer] = useState("");
  const [score, setScore] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.submitScore(pid, board.id, player.trim(), Number(score));
      toast(`${player.trim()} → rank ${res.rank}${res.previous_rank ? ` (was ${res.previous_rank})` : ""}`);
      onDone();
    } catch (ex) {
      toast((ex as Error).message, "err");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Submit a score" onClose={onClose}>
      <form className="modal-body" onSubmit={submit}>
        <p className="muted" style={{ fontSize: 13.5, marginTop: -4 }}>
          Submit a score for <span className="mono">{board.id}</span>. Useful for testing — your game would normally call the data-plane API directly.
        </p>
        <div className="field">
          <label htmlFor="pl">Player id</label>
          <input id="pl" className="input mono" autoFocus value={player} onChange={(e) => setPlayer(e.target.value)} placeholder="player-42" required />
        </div>
        <div className="field">
          <label htmlFor="sc">Score {board.score_type === "f64" ? "(float)" : "(integer)"}</label>
          <input id="sc" className="input mono" type="number" step={board.score_type === "f64" ? "any" : "1"}
            value={score} onChange={(e) => setScore(e.target.value)} placeholder="184220" required />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !player.trim() || score === ""}>
            {busy ? <span className="spinner" /> : "Submit"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
